/*
 * Lemonade Training Oy, copy rights, all right reserved.
 *
 * The locked cancel-flow copy deck. These strings are legally load-bearing —
 * the statutory assurances, the certificate line and the gym-visibility
 * disclosure are what make the flow compliant — so they live in one module
 * rather than inline, where a well-meaning edit in one lane would silently
 * diverge from the deck the design and mobile share.
 */

import type { CancelLane } from "@lemonadetraining/contracts";

export type { CancelLane };

export const CANCEL_ENTRY_LABEL = "Cancel membership";

export const FLOW_TITLE = "Cancel membership";

export const REASON_PROMPT = "We're sorry to see you thinking about leaving. What's going on?";

export const REASON_REASSURANCE = "Your membership stays active until you finish this.";

export const REASON_OPTIONS: Array<{ lane: CancelLane; step: string; label: string }> = [
  { lane: "default", step: "confirm", label: "My commitment is ending, or I just want to stop" },
  {
    lane: "statutory",
    step: "lane-a",
    label: "Something serious has changed — illness or pregnancy",
  },
  { lane: "goodwill", step: "lane-b", label: "I'm moving away, or a personal reason" },
];

/* ---------------- Default lane (ends at the commitment end date) ---------- */

export const DEFAULT_HEADING = "End at your commitment end date";

export const defaultLaneDetail = (endDate: string) =>
  `Your membership will end on ${endDate}. You keep full access until then.`;

export const defaultLanePayments = (paymentsLeft: number) =>
  `You'll be billed ${paymentsLeft} more ${paymentsLeft === 1 ? "time" : "times"} before it ends.`;

export const DEFAULT_UPFRONT_NOTE =
  "You paid for this term upfront, so there are no more payments.";

export const defaultConfirmConsequence = (endDate: string) =>
  `Your membership will end on ${endDate}. You keep access until then, and you can change your mind any time before that date.`;

export const DEFAULT_CONFIRM_ACTION = "End at my end date";

export const DEFAULT_PAYMENTS_UNKNOWN =
  "We'll show your remaining payments on your memberships page.";

export const DEFAULT_DONE_TITLE = "Your membership is set to end";

export const defaultLaneDone = (endDate: string) =>
  `Scheduled to end ${endDate} — you have access until then.`;

export const DEFAULT_DONE_DETAIL =
  "Changed your mind? You can keep your membership from your memberships page any time before that date.";

/* ---------------- Lane A (statutory illness / pregnancy) ------------------ */

export const LANE_A_HEADING = "Illness or pregnancy";

export const LANE_A_INTRO =
  "You have a legal right to end your membership early. We'll take you at your word.";

/**
 * The deck's Lane A assurances, split at their own sentence seam.
 *
 * "cannot block it" is FALSE for a gym configured to review these requests, and
 * a false assurance about a legal right is the one thing this lane cannot ship
 * — so the timing claim is chosen by the gym's mode while the privacy claim,
 * true in every mode, is always shown. When the mode cannot be read, the
 * timing claim is simply omitted: no sentence is better than the wrong one,
 * and the confirmation page states what actually happened either way.
 * Strict mode renders the deck string verbatim.
 */
export const LANE_A_ASSURANCE_STRICT_TIMING =
  "This takes effect now. Your gym is notified but cannot block it.";

export const LANE_A_ASSURANCE_OWNER_REVIEW_TIMING =
  "Your gym reviews requests like this before they take effect, so this one goes to them rather than ending your membership straight away.";

export const LANE_A_ASSURANCE_PRIVACY =
  "Your gym never sees your diagnosis — only that a valid reason was confirmed.";

/** Verbatim, per the ratified no-document-handling decision. */
export const CERTIFICATE_NOTE =
  "Keep your certificate — your gym may ask to see it directly if they contest.";

export const LANE_A_GROUND_LEGEND = "What has changed?";

export const LANE_A_GROUNDS: Array<{ value: "illness" | "pregnancy"; label: string }> = [
  { value: "illness", label: "Illness" },
  { value: "pregnancy", label: "Pregnancy" },
];

export const LANE_A_DECLARATION =
  "I confirm that I am ending this membership because of illness or pregnancy, and that this declaration is true.";

export const LANE_A_TIMING_LEGEND = "When should it end?";

export const LANE_A_TIMING_NOW = "Immediately";

export const LANE_A_TIMING_DATE = "On a date I choose";

export const LANE_A_DATE_LABEL = "End date";

export const LANE_A_ACTION = "End my membership";

export const LANE_A_GROUND_MISSING = "Please choose what has changed.";

/** Matches the server's own refusal, so the two can never contradict. */
export const LANE_A_DECLARATION_MISSING =
  "Please confirm the declaration before ending your membership.";

export const LANE_A_DATE_MISSING = "Please choose the date your membership should end.";

export const LANE_A_DATE_OUT_OF_RANGE = (first: string, last: string) =>
  `Choose a date between ${first} and ${last}.`;

/** Shown beside the paused submit while a dated end waits for the gym's zone. */
export const LANE_A_GYM_CALENDAR_LOADING = "Loading your gym's calendar…";

/** The term changed after a date was picked, and no date can be chosen now. */
export const LANE_A_DATE_UNAVAILABLE =
  "An end date can no longer be chosen for this membership. Choose Immediately, or contact your gym.";

export const LANE_A_CONFIRM_CONSEQUENCE_STRICT =
  "This ends your membership and cannot be undone in the app. Any refund owed is worked out and sent automatically.";

export const LANE_A_CONFIRM_CONSEQUENCE_OWNER_REVIEW =
  "This sends your declaration to your gym for review. Your membership and billing continue until they answer.";

/**
 * The same rule the assurance above follows, applied to the last screen before
 * an irreversible-looking action: when the gym's mode cannot be read, claim
 * NOTHING about timing or finality.
 *
 * Collapsing an unknown mode into the strict wording is the worst reachable
 * outcome in this lane — a member of an owner_review gym would be told their
 * membership has ended and their money is on its way at the exact moment they
 * commit, when nothing has ended, billing continues and no money moves.
 */
export const LANE_A_CONFIRM_CONSEQUENCE_UNKNOWN =
  "This submits your declaration now. We'll tell you exactly what happened, and what it means for your billing, on the next screen.";

export const LANE_A_DONE_TITLE = "Your membership has ended";

export const LANE_A_DONE = "Done. Your membership has ended. Any refund owed is on its way.";

export const LANE_A_PENDING_TITLE = "Sent to your gym";

export const LANE_A_PENDING =
  "Sent. Your gym will review your request and we'll email you when they respond.";

/* ---------------- Lane B (goodwill relocation / personal) ----------------- */

export const LANE_B_HEADING = "Moving or a personal reason";

export const LANE_B_EXPECTATION =
  "Early exits for these reasons are up to your gym. We'll send your request and let you know — usually within a few days.";

export const LANE_B_NOTE_LABEL = "Tell your gym what's going on (optional)";

export const LANE_B_DISCLOSURE =
  "Your gym will read this. Please leave out any medical details — you do not need them here.";

export const LANE_B_WAITING =
  "While you wait, your membership stays active and billing continues as normal.";

export const LANE_B_ACTION = "Send request";

export const LANE_B_FALLBACK =
  "Not what you meant? You can still cancel at your commitment end date instead.";

export const LANE_B_FALLBACK_ACTION = "Cancel at my end date instead";

export const LANE_B_DONE_TITLE = "Request sent";

export const LANE_B_DONE = "Request sent. We'll email you when your gym responds.";

/* ---------------- Shared ------------------------------------------------- */

export const SAFE_ACTION_LABEL = "Go back";

export const BACK_TO_MEMBERSHIP = "Back to my memberships";

export const SUBMIT_FAILED_TITLE = "We could not complete that";

export const SUBMIT_FAILED_FALLBACK =
  "Something went wrong on our side and nothing was changed. Please try again in a moment.";

/**
 * The marker-only document: a purchase whose provisioning has not landed yet,
 * so there is no membership product to name in a cancellation. The entry stays
 * visible regardless — a hidden cancel button is the thing the law sanctions —
 * and this is what it honestly leads to.
 */
export const STILL_PROVISIONING_TITLE = "We're still setting up this membership";

export const STILL_PROVISIONING_DETAIL =
  "We can't process a cancellation until that finishes. This usually only takes a moment — please try again shortly. If it doesn't complete, your gym can help.";

export const NOT_FOUND_TITLE = "We could not find that membership";

export const NOT_FOUND_DETAIL = "It may already have ended, or the link may be out of date.";
